<template>
  <div
    class="rt-date-picker"
    @mouseenter="inputHovering = true"
    @mouseleave="inputHovering = false"
  >
    <el-date-picker
      ref="input"
      :class="{ 'rt-xpc-date-close__bg': showCloseBg && showClose }"
      :value="computedValue"
      v-bind="$attrs"
      :clearable="false"
      :type="type"
      :disabled="computedDisabled"
      :readonly="readonly"
      :placeholder="computedSelectPlaceholder"
      :start-placeholder="computedStartPlaceholder"
      :end-placeholder="computedEndPlaceholder"
      :footer-align="footerAlign"
      :stepper="stepper"
      :click-now-show-picker="clickNowShowPicker"
      @input="inputHandler"
      @change="onChangeHandler"
      @blur="onBlurHandler"
      @focus="onFocusHandler"
      @date-confirm="handleDatetimeConfirm"
      @datetime-cancel="handleDatetimeCancel"
      @date-range-confirm="handleDatetimeRangeConfirm"
      @shortcut="handleShortcut"
      @keydown="handleKeyEnter"
      @visible-change="handlePickerVisible"
      @change-now="handleChangeToNow"
    >
      <div
        v-if="$slots['header']"
        slot="date-header"
      >
        <slot name="header"></slot>
      </div>
    </el-date-picker>
    <rt-icon
      v-show="!computedDisabled && computedSuffixIcon"
      name="rt-base-date"
      @click="inputFocus"
    />
    <rt-icon
      v-if="showClose"
      :name="computedIconName"
      :style="computedClearIconStyle"
      @click="clear"
    />
  </div>
</template>

<script>
import inputFocusMixins from './inputFocusMixins.js';
import RtIcon from '../icon/rtIcon.vue';
import ElDatePicker from 'vue-xpc/src/element-ui/packages/date-picker/picker/elDatePicker.vue';
import xpcConfig from '../../config';
import { throttle } from 'lodash';
import Placeholder from './placeholder.js';

export default {
  name: 'RtDatePicker',
  components: { RtIcon, ElDatePicker },
  mixins: [inputFocusMixins, Placeholder],
  props: {
    value: {
      type: [Date, String, Array],
      default: null
    },
    type: {
      type: String,
      default: 'date'
    },
    datafieldname: String,
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    startPlaceholder: String,
    endPlaceholder: String,
    // footer 按钮的对齐方式，默认 center、可选 left、right
    footerAlign: {
      type: String,
      default: 'center'
    },
    // 自定义清空按钮图标
    clearIconName: {
      type: String,
      default: undefined
    },
    // 自定义清空按钮位置
    clearIconRight: {
      type: Number,
      default: 0
    },
    // 分钟 / 秒跨度
    stepper: [Number, Boolean],
    // 点击 <此刻> 不关闭 picker
    clickNowShowPicker: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      inputValue: null,
      inputHovering: false,
      datetimeValue: null,
      dateValue: null,
      validDateType: ['date', 'year', 'month', 'week'].includes(this.type)
    };
  },
  inject: {
    elForm: {
      default: ''
    }
  },
  computed: {
    computedValue () {
      if (this.validDateType && !this.isValidDate(this.value) && !this.valueIsEmpty()) {
        this.dateValue = null;
        this.$emit('input', this.dateValue);
      }
      if (
        (this.type === 'datetime' && !this.isValidDate(this.value)) ||
        (this.value &&
          this.value.length > 0 &&
          this.type === 'datetimerange' &&
          !this.isValidDate(this.value[0]))
      ) {
        this.datetimeValue = null;
        this.$emit('input', this.datetimeValue);
      }
      return this.validDateType ? this.dateValue : this.datetimeValue;
    },
    /**
     * 当清空按钮设置偏移量的时候，显示后缀图标
     */
    computedSuffixIcon () {
      return (
        this.clearIconRight ||
        xpcConfig.get('clearicon.clearIconRight') >= 24 ||
        !this.showClose
      );
    },
    computedIconName () {
      return this.clearIconName || xpcConfig.get('clearicon.clearIconName');
    },
    computedClearIconStyle () {
      let style = {};
      style['marginRight'] =
        this.clearIconRight || xpcConfig.get('clearicon.clearIconRight') + 'px';
      return style;
    },
    showCloseBg () {
      return this.clearIconRight || xpcConfig.get('clearicon.clearIconRight');
    },
    computedDisabled () {
      return this.disabled || (this.elForm || {}).disabled;
    },
    showClose () {
      if (this.validDateType) {
        return !this.computedDisabled && this.inputHovering && this.dateValue;
      }
      return !this.computedDisabled && this.inputHovering && this.datetimeValue;
    }
  },
  watch: {
    value (newVal) {
      if (newVal) {
        this.initValue(newVal);
      } else {
        this.inputValue = null;
        this.datetimeValue = null;
        this.dateValue = null;
      }
    }
  },
  created: function () {
    this.initValue(this.value);
  },
  methods: {
    valueIsEmpty () {
      return this.value === undefined || this.value === null || this.value === '';
    },
    initValue (value) {
      if (this.$xpc.isArray(value)) {
        if (value.length > 0) {
          this.inputValue = [new Date(value[0]), new Date(value[1])];
          this.datetimeValue = [new Date(value[0]), new Date(value[1])];
        } else {
          this.inputValue = null;
          this.datetimeValue = null;
        }
      } else if (value) {
        this.inputValue = value;
        this.datetimeValue = value;
        this.dateValue = value;
      }
    },
    isValidDate (date) {
      const newDate = new Date(date);
      return !isNaN(newDate.getTime());
    },
    handleChangeToNow (val) {
      if (!this.clickNowShowPicker) {
        this.$emit('input', val);
      }
    },
    handlePickerVisible (val) {
      if (val) {
        this.inputValue = this.datetimeValue;
      }
    },
    _setDatetimeValue () {
      this.datetimeValue = this.inputValue;
      this.$emit('input', this.datetimeValue);
    },
    handleKeyEnter () {
      this._setDatetimeValue();
    },
    handleShortcut () {
      this._setDatetimeValue();
    },
    handleDatetimeCancel () {
      this.$emit('input', this.datetimeValue, null, this.datafieldname);
    },
    handleDatetimeConfirm (value) {
      this.inputValue = value;
      this._setDatetimeValue();
    },
    handleDatetimeRangeConfirm () {
      this._setDatetimeValue();
    },
    inputHandler (newValue) {
      this.inputValue = newValue;
      if (this.validDateType) {
        this.$emit('input', newValue, null, this.datafieldname);
        this.emitChange(newValue, this);
      }
    },
    clear () {
      this.datetimeValue = null;
      this.$emit('input', this.datetimeValue, null, this.datafieldname);
      this.emitChange('', this);
    },
    onChangeHandler (newValue) {
      this.emitChange(newValue, this);
    },
    onBlurHandler (el) {
      this.$emit('blur', el);
    },
    onFocusHandler (el) {
      this.$emit('focus', el);
    },
    emitChange: throttle((newValue, _self) => {
      if (_self) {
        _self.$emit('change', newValue, null, _self.datafieldname);
      }
    }, 300)
  }
};
</script>



// WEBPACK FOOTER //
// rtDatePicker.vue?067b488d